/**
 * Colorsets Swatches - Dynamic Swatch Injection for Product Cards
 *
 * This script provides functionality to dynamically add colorway swatches to product cards
 * that don't have access to metaobjects on the backend (e.g., Rebuy, GrapheneHC cards).
 *
 * Usage:
 * 1. Include this script after the colorsets.liquid snippet
 * 2. Initialize with ColorsetSwatches.init()
 * 3. Use ColorsetSwatches.applySwatches() to process existing cards
 * 4. The script will automatically handle new cards via MutationObserver
 */

class ColorsetSwatches {
  constructor() {
    this.colorsetData = null;
    this.productToColorsetMap = new Map();
    this.observer = null;
    this.isInitialized = false;
    this.loadingTargets = new Set(); // Track containers that are loading
    this.pendingApplications = new Set(); // Track elements waiting for application
  }

  /**
   * Initialize the colorset swatches system
   * This runs asynchronously and won't block page load
   */
  async init() {
    //console.log('🎨 Starting ColorsetSwatches initialization...');
    try {
      //console.log('🎨 Step 1: Loading colorset data asynchronously...');
      await this.loadColorsetData();
      //console.log('🎨 Step 2: Building product map...');
      this.buildProductMap();
      //console.log('🎨 Step 3: Setting up mutation observer...');
      this.setupMutationObserver();
      //console.log('🎨 Step 4: Applying swatches to existing cards...');
      await this.applySwatches();
      //console.log('🎨 Step 5: Initializing integrations...');
      this.initializeIntegrations();
      this.isInitialized = true;
      // console.log('🎨 ColorsetSwatches initialized successfully');
    } catch (error) {
      /*
      console.warn(
        '🎨 ColorsetSwatches initialization failed (non-blocking):',
        error.message,
      );
			*/
      // Don't throw the error - let the page continue to work without colorsets
      // The system will gracefully degrade
    }
  }

  /**
   * Load colorset data from shop metafield
   * This allows the main page to load quickly while colorsets load in the background
   */
  async loadColorsetData() {
    // console.log('🎨 Loading colorsets data from shop metafield...');

    try {
      // Wait for window.theme to be available (it's defined inline in theme.liquid)
      let attempts = 0;
      const maxAttempts = 50; // Wait up to 5 seconds (50 * 100ms)

      while (
        (typeof window.theme === 'undefined' ||
          !window.theme?.shop?.metafields?.custom?.colorsets) &&
        attempts < maxAttempts
      ) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }

      // Check if metafield is available in the theme
      if (
        typeof window.theme !== 'undefined' &&
        window.theme.shop?.metafields?.custom?.colorsets
      ) {
        const metafieldData = window.theme.shop.metafields.custom.colorsets;

        // console.log('🎨 Raw metafield data type:', typeof metafieldData);
        // console.log('🎨 Raw metafield data:', metafieldData);

        // Parse the metafield value if it's a string
        let parsedData;
        if (typeof metafieldData === 'string') {
          try {
            parsedData = JSON.parse(metafieldData);
          } catch (parseError) {
            console.error('🎨 Failed to parse metafield JSON:', parseError);
            throw new Error('Invalid JSON in metafield');
          }
        } else {
          parsedData = metafieldData;
        }

        // console.log('🎨 Parsed data:', parsedData);
        // console.log('🎨 Has colorsets?', !!parsedData?.colorsets);
        /*
        console.log(
          'ðŸŽ¨ Is colorsets array?',
          Array.isArray(parsedData?.colorsets),
        );
        */

        // Validate that we have colorsets data
        if (parsedData?.colorsets && Array.isArray(parsedData.colorsets)) {
          this.colorsetData = parsedData;
          /*
          console.log(
            '🎨 Metafield data loaded successfully - Colorsets count:',
            this.colorsetData.colorsets.length,
          );
					*/
          return;
        } else {
          /*
          console.warn(
            '🎨 Metafield does not contain valid colorsets data:',
            parsedData,
          );
					*/
          throw new Error('Metafield does not contain valid colorsets data');
        }
      } else {
        /*
        console.log(
          '🎨 Window.theme object:',
          typeof window.theme !== 'undefined' ? window.theme : 'undefined',
        );
        console.log('🎨 Window.theme.shop:', window.theme?.shop);
        console.log(
          '🎨 Window.theme.shop.metafields:',
          window.theme?.shop?.metafields,
        );
        console.log(
          '🎨 Window.theme.shop.metafields.custom:',
          window.theme?.shop?.metafields?.custom,
        );
        console.log(
          '🎨 Window.theme.shop.metafields.custom.colorsets:',
          window.theme?.shop?.metafields?.custom?.colorsets,
        );
				*/
        throw new Error('Shop metafield custom.colorsets not found');
      }
    } catch (error) {
      console.error(
        '🎨 Failed to load colorsets from metafield:',
        error.message,
      );
      throw error;
    }
  }

  /**
   * Build a map of product handles/IDs to their colorset information
   */
  buildProductMap() {
    if (!this.colorsetData) return;

    // Use the new products structure if available, fallback to colorsets structure
    if (this.colorsetData.products && this.colorsetData.products.length > 0) {
      /*
			console.log(
        `🎨 Building product map from products structure (${this.colorsetData.products.length} products)`,
      );
			*/
      this.buildProductMapFromProducts();
    } else if (
      this.colorsetData.colorsets &&
      this.colorsetData.colorsets.length > 0
    ) {
      /*
      console.log(
        `🎨 Building product map from colorsets structure (${this.colorsetData.colorsets.length} colorsets)`,
      );
			*/
      this.buildProductMapFromColorsets();
    }

    /*
    console.log(
      `🎨 Product map built: ${this.productToColorsetMap.size} entries`,
    );
		*/
    // Log first 5 handles in map for debugging
    const sampleHandles = Array.from(this.productToColorsetMap.keys()).slice(
      0,
      5,
    );
    // console.log(`🎨 Sample product handles in map:`, sampleHandles);
  }

  /**
   * Build product map using the new products structure
   */
  buildProductMapFromProducts() {
    // Create a map of colorset handle to colorset data for quick lookup
    const colorsetMap = new Map();
    this.colorsetData.colorsets.forEach((colorset) => {
      colorsetMap.set(colorset.handle, colorset);
    });

    // console.log(`🎨 Colorset map built: ${colorsetMap.size} colorsets`);

    // Create a lookup map of products by handle and ID from colorsetData.products
    // This ensures we get the full product data including swatch property
    const productsLookup = new Map();
    this.colorsetData.products.forEach((product) => {
      if (product.handle) {
        productsLookup.set(product.handle, product);
      }
      if (product.id) {
        productsLookup.set(product.id.toString(), product);
        // Also handle numeric IDs
        if (typeof product.id === 'string' && !isNaN(product.id)) {
          productsLookup.set(parseInt(product.id, 10).toString(), product);
        }
      }
    });

    // console.log(`🎨 Products lookup map built: ${productsLookup.size} entries`);

    // Process each product and build its swatch data
    let mappedCount = 0;
    let skippedCount = 0;
    this.colorsetData.products.forEach((productData) => {
      if (!productData.colorsets || productData.colorsets.length === 0) {
        skippedCount++;
        return;
      }

      // Collect all products from all colorsets this product belongs to
      // Use Map to track by handle to avoid duplicates and get full product data
      const allProductsMap = new Map();

      productData.colorsets.forEach((colorsetHandle) => {
        // Handle both string handles and object handles
        const handle =
          typeof colorsetHandle === 'string'
            ? colorsetHandle
            : colorsetHandle.handle || colorsetHandle.id;

        const colorset = colorsetMap.get(handle);
        if (colorset && colorset.products) {
          colorset.products.forEach((product) => {
            // Look up the full product data from colorsetData.products to get swatch property
            const fullProduct =
              productsLookup.get(product.handle) ||
              productsLookup.get(product.id?.toString()) ||
              product; // Fallback to product from colorset if not found

            // Use handle as key to avoid duplicates
            if (fullProduct.handle) {
              allProductsMap.set(fullProduct.handle, fullProduct);
            } else if (product.handle) {
              allProductsMap.set(product.handle, fullProduct);
            }
          });
        }
      });

      const allProductsArray = Array.from(allProductsMap.values());

      // Map by product handle (primary key)
      if (productData.handle) {
        this.productToColorsetMap.set(productData.handle, {
          product: productData,
          colorsets: productData.colorsets
            .map((handle) => {
              const h =
                typeof handle === 'string'
                  ? handle
                  : handle.handle || handle.id;
              return colorsetMap.get(h);
            })
            .filter(Boolean),
          allProducts: allProductsArray,
          // Keep backward compatibility
          colorset: (() => {
            const firstHandle = productData.colorsets[0];
            const h =
              typeof firstHandle === 'string'
                ? firstHandle
                : firstHandle.handle || firstHandle.id;
            return colorsetMap.get(h);
          })(), // First colorset for backward compatibility
        });
        mappedCount++;
      }

      // Also map by product ID for fallback
      if (productData.id) {
        this.productToColorsetMap.set(productData.id.toString(), {
          product: productData,
          colorsets: productData.colorsets
            .map((handle) => {
              const h =
                typeof handle === 'string'
                  ? handle
                  : handle.handle || handle.id;
              return colorsetMap.get(h);
            })
            .filter(Boolean),
          allProducts: allProductsArray,
          // Keep backward compatibility
          colorset: (() => {
            const firstHandle = productData.colorsets[0];
            const h =
              typeof firstHandle === 'string'
                ? firstHandle
                : firstHandle.handle || firstHandle.id;
            return colorsetMap.get(h);
          })(), // First colorset for backward compatibility
        });
      }
    });
    /*
    console.log(
      `🎨 Mapped ${mappedCount} products, skipped ${skippedCount} products without colorsets`,
    );
		*/
  }

  /**
   * Build product map using the legacy colorsets structure (fallback)
   */
  buildProductMapFromColorsets() {
    // Create a lookup map of products by handle and ID from colorsetData.products
    // This ensures we get the full product data including swatch property
    const productsLookup = new Map();
    if (this.colorsetData.products) {
      this.colorsetData.products.forEach((product) => {
        if (product.handle) {
          productsLookup.set(product.handle, product);
        }
        if (product.id) {
          productsLookup.set(product.id.toString(), product);
          // Also handle numeric IDs
          if (typeof product.id === 'string' && !isNaN(product.id)) {
            productsLookup.set(parseInt(product.id, 10).toString(), product);
          }
        }
      });
    }

    // First pass: collect all products and their colorsets
    const productColorsetsMap = new Map();

    this.colorsetData.colorsets.forEach((colorset) => {
      if (!colorset.products) return;

      colorset.products.forEach((product) => {
        // Initialize arrays for this product if not exists
        if (!productColorsetsMap.has(product.handle)) {
          productColorsetsMap.set(product.handle, {
            product: product,
            colorsets: [],
            allProducts: new Map(), // Use Map to track by handle and get full product data
          });
        }

        const productData = productColorsetsMap.get(product.handle);

        // Add this colorset to the product's colorsets
        productData.colorsets.push(colorset);

        // Add all products from this colorset to the combined map
        // Look up full product data to get swatch property
        colorset.products.forEach((p) => {
          const fullProduct =
            productsLookup.get(p.handle) ||
            productsLookup.get(p.id?.toString()) ||
            p; // Fallback to product from colorset if not found

          if (fullProduct.handle) {
            productData.allProducts.set(fullProduct.handle, fullProduct);
          } else if (p.handle) {
            productData.allProducts.set(p.handle, fullProduct);
          }
        });
      });
    });

    // Second pass: build the final map with all colorsets per product
    productColorsetsMap.forEach((productData, productHandle) => {
      // Convert Map to Array for allProducts, using full product data
      const allProducts = Array.from(productData.allProducts.values());

      // Map by product handle (primary key)
      this.productToColorsetMap.set(productHandle, {
        product: productData.product,
        colorsets: productData.colorsets,
        allProducts: allProducts,
        // Keep backward compatibility
        colorset: productData.colorsets[0], // First colorset for backward compatibility
      });

      // Also map by product ID for fallback
      this.productToColorsetMap.set(productData.product.id.toString(), {
        product: productData.product,
        colorsets: productData.colorsets,
        allProducts: allProducts,
        // Keep backward compatibility
        colorset: productData.colorsets[0], // First colorset for backward compatibility
      });
    });
  }

  /**
   * Extract product identifier from various card formats
   */
  extractProductIdentifier(cardElement) {
    // Try data-handle attribute first (used by Advanced Commerce/GrapheneHC cards)
    const dataHandle = cardElement.getAttribute('data-handle');
    if (dataHandle) {
      return dataHandle; // product handle
    }

    // Try to get product URL from data-product-url attribute (used by Dawn cards)
    const productUrlElement = cardElement.querySelector('[data-product-url]');
    if (productUrlElement) {
      const url = productUrlElement.getAttribute('data-product-url');
      const match = url.match(/\/products\/([^?#]+)/);
      if (match) {
        return match[1]; // product handle
      }
    }

    // Try to get product ID and convert to handle (fallback)
    const productIdElement = cardElement.querySelector('[data-product-id]');
    if (productIdElement) {
      const productId = productIdElement.getAttribute('data-product-id');
      // Try to find product handle in colorset map by ID
      if (this.productToColorsetMap.has(productId)) {
        return productId; // Return ID if it exists in map
      }
    }

    // Try data-id attribute (Advanced Commerce also uses this)
    const dataId = cardElement.getAttribute('data-id');
    if (dataId && this.productToColorsetMap.has(dataId)) {
      return dataId; // Return ID if it exists in map
    }

    // Try to extract from href links (fallback)
    const productLink = cardElement.querySelector('a[href*="/products/"]');
    if (productLink) {
      const href = productLink.getAttribute('href');
      const match = href.match(/\/products\/([^?#]+)/);
      if (match) {
        return match[1]; // product handle
      }
    }

    return null;
  }

  /**
   * Generate swatch HTML based on colorset data
   */
  generateSwatchHTML(colorsetInfo, currentProductHandle) {
    // Get all products from all colorsets this product belongs to
    const allProducts = colorsetInfo.allProducts;
    let swatchesUsed = 0;
    const maxSwatches = 10;

    let swatchesHTML = '';
    let additionalCount = 0;
    const processedProducts = new Set(); // Track processed products to avoid duplicates

    // Process products to create swatches
    for (const product of allProducts) {
      // Skip if we've already processed this product
      if (processedProducts.has(product.handle)) {
        continue;
      }

      if (swatchesUsed < maxSwatches) {
        const swatchHTML = this.createSwatchItem(product, currentProductHandle);
        if (swatchHTML) {
          swatchesHTML += swatchHTML;
          swatchesUsed++;
          processedProducts.add(product.handle);
        }
      } else {
        additionalCount++;
      }
    }

    // Build the complete secondary div HTML
    if (swatchesUsed > 0) {
      return `
        <ul class='tw-flex tw-gap-[4px] tw-items-end tw-justify-start tw-justify-content-start'>
          ${swatchesHTML}
        </ul>
        ${
          additionalCount > 0
            ? `<div class='tw-hidden tw-text-[10px] tw-leading-[12px] tw-text-[#767676] tw-text-right'>+ ${additionalCount} styles</div>`
            : ''
        }
      `;
    }

    return '';
  }

  /**
   * Additional initialization for specific integrations
   */
  initializeIntegrations() {
    document.addEventListener('DOMContentLoaded', function () {
      // Rebuy integration - refresh swatches when Rebuy content loads
      if (window.rebuy) {
        const originalRender = window.rebuy.render;
        window.rebuy.render = function () {
          originalRender.apply(this, arguments);
          setTimeout(() => {
            if (
              window.ColorsetSwatches &&
              window.ColorsetSwatches.isInitialized
            ) {
              window.ColorsetSwatches.refresh();
            }
          }, 500);
        };
      }

      // GrapheneHC integration - refresh swatches when GrapheneHC content loads
      document.addEventListener('graphene:loaded', function () {
        setTimeout(() => {
          if (
            window.ColorsetSwatches &&
            window.ColorsetSwatches.isInitialized
          ) {
            window.ColorsetSwatches.refresh();
          }
        }, 500);
      });

      // Advanced Commerce integration - hook into GrapheneHC success callbacks
      const hookGrapheneCallbacks = () => {
        if (window.$g && typeof window.$g.load === 'function') {
          // Store original $g.load method
          const original$gLoad = window.$g.load;
          window.$g.load = function (config) {
            // Wrap success callback
            const originalOnSuccess = config.onSuccess;
            config.onSuccess = function (...args) {
              // Call original success callback first
              if (originalOnSuccess) {
                originalOnSuccess.apply(this, args);
              }

              // Then refresh swatches with a small delay
              setTimeout(() => {
                if (
                  window.ColorsetSwatches &&
                  window.ColorsetSwatches.isInitialized
                ) {
                  window.ColorsetSwatches.refresh();
                }
              }, 300);
            };

            // Call original load method with modified config
            return original$gLoad.call(this, config);
          };
        } else {
          // Try again if $g isn't available yet
          setTimeout(hookGrapheneCallbacks, 500);
        }
      };

      // Start hooking after a delay to ensure $g is loaded
      setTimeout(hookGrapheneCallbacks, 1000);

      // Infinite Scroll integration - refresh swatches when new products load
      document.addEventListener('endlessScroll:loaded', function (event) {
        if (window.ColorsetSwatches && window.ColorsetSwatches.isInitialized) {
          setTimeout(() => {
            window.ColorsetSwatches.refresh();
          }, 300);
        }
      });

      // Monitor for EndlessScroll initialization and hook into it
      const checkEndlessScroll = () => {
        if (
          window.endlessScroll &&
          typeof window.endlessScroll.refresh === 'function'
        ) {
          // Store original methods if they exist
          if (
            window.endlessScroll.loadMore &&
            !window.endlessScroll._colorsetHooked
          ) {
            const originalLoadMore = window.endlessScroll.loadMore.bind(
              window.endlessScroll,
            );
            window.endlessScroll.loadMore = async function (...args) {
              const result = await originalLoadMore(...args);
              if (
                window.ColorsetSwatches &&
                window.ColorsetSwatches.isInitialized
              ) {
                setTimeout(() => {
                  window.ColorsetSwatches.refresh();
                }, 500);
              }
              return result;
            };
            window.endlessScroll._colorsetHooked = true;
          }
        } else {
          // Check again in a bit
          setTimeout(checkEndlessScroll, 1000);
        }
      };

      // Start checking for EndlessScroll
      setTimeout(checkEndlessScroll, 2000);

      // Generic AJAX content loading - refresh swatches on common events
      const refreshEvents = ['DOMNodeInserted', 'ajaxComplete', 'load'];
      refreshEvents.forEach((eventName) => {
        document.addEventListener(eventName, function () {
          if (
            window.ColorsetSwatches &&
            window.ColorsetSwatches.isInitialized
          ) {
            clearTimeout(window.colorsetRefreshTimeout);
            window.colorsetRefreshTimeout = setTimeout(() => {
              window.ColorsetSwatches.refresh();
            }, 300);
          }
        });
      });
    });
  }

  /**
   * Extract image URL from swatch property
   * Handles both string URLs and object structures
   */
  extractSwatchImage(swatch) {
    if (!swatch) {
      return null;
    }

    // If swatch is a string URL, return it directly
    if (typeof swatch === 'string') {
      return swatch;
    }

    // If swatch is an object, try common image URL properties
    if (typeof swatch === 'object') {
      // Try various possible property names
      return (
        swatch.url ||
        swatch.image_url ||
        swatch.imageUrl ||
        swatch.src ||
        swatch.image?.url ||
        swatch.image?.image_url ||
        null
      );
    }

    return null;
  }

  /**
   * Create individual swatch item HTML
   */
  createSwatchItem(product, currentProductHandle) {
    // Skip the current product
    if (product.handle === currentProductHandle) {
      return '';
    }

    // Extract swatch image from product.swatch property
    const swatchImage = this.extractSwatchImage(product.swatch);

    // If no swatch image found, skip this swatch
    if (!swatchImage || swatchImage === '') {
      return '';
    }

    const swatchTitle = product.title || product.handle || '';
    const productUrl = product.url || `/products/${product.handle}`;

    // Ensure proper image sizing for Shopify CDN URLs with responsive srcset
    let finalSwatchImage = swatchImage;
    if (swatchImage.includes('cdn.shopify.com')) {
      // Remove existing query params and add optimized sizes
      const baseUrl = swatchImage.split('?')[0];
      // Use srcset for responsive sizing: 16px (mobile), 20px (tablet), 24px (desktop)
      // Base src uses 16px (smallest size for mobile-first)
      finalSwatchImage = baseUrl + '?width=16&height=12&crop=center';
    }

    return `
      <li class='product-swatch-item tw-flex tw-flex-col tw-items-center tw-justify-center tw-overflow-hidden tw-w-[14px] tw-h-[10px] tw-rounded-[2px] hover:tw-scale-110 tw-transition-all tw-duration-300'>
        <button class='product-swatch-button tw-flex tw-items-center tw-justify-center tw-p-0 tw-border-0 tw-rounded-[2px] tw-bg-transparent tw-cursor-pointer tw-transition-transform tw-duration-200 tw-w-full tw-h-full tw-overflow-hidden hover:tw-scale-110'>
					<img 
            class='tw-size-full tw-rounded-[1px] tw-image-cover tw-overflow-hidden tw-pointer-events-none'
            src='${finalSwatchImage}'
            srcset='${
              swatchImage.includes('cdn.shopify.com')
                ? swatchImage.split('?')[0] +
                  '?width=16&height=12&crop=center 16w, ' +
                  swatchImage.split('?')[0] +
                  '?width=20&height=20&crop=center 20w, ' +
                  swatchImage.split('?')[0] +
                  '?width=24&height=24&crop=center 24w'
                : finalSwatchImage
            }'
            sizes='16px'
            alt='${swatchTitle}'
            width='16'
            height='12'
            loading='lazy'
            data-product-url='${productUrl}'
          >
          <div class='loading__spinner hidden' style='position: absolute; z-index: 1; width: 12px; height: 12px;'>
            <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" style='width: 12px; height: 12px;'>
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30" stroke="#000"></circle>
            </svg>
          </div>
        </button>
      </li>
    `;
  }

  /**
   * Apply swatches to a single product card
   */
  applySwatchesToCard(cardElement) {
    // Debug: Check if this is a shop-the-look card
    if (
      cardElement.classList.contains('shop-the-look-card') ||
      cardElement.closest('.shop-the-look-card')
    ) {
      return;
    }

    // Additional debug: Log if we're about to add has-swatches class
    const cardContent = cardElement.querySelector('.card__content');

    // Check if this card already has swatches
    const existingSwatches = cardElement.querySelector('.colorset-swatches');
    if (existingSwatches) {
      return; // Already processed
    }

    // Extract product identifier first
    const productIdentifier = this.extractProductIdentifier(cardElement);
    if (!productIdentifier) {
      return; // Couldn't identify product
    }

    // Get colorset info for this product
    const colorsetInfo = this.productToColorsetMap.get(productIdentifier);
    if (!colorsetInfo) {
      return; // Product not in any colorset
    }

    // Find or create a place to inject swatches
    let targetInfo = this.findSwatchTarget(cardElement);
    if (!targetInfo || !targetInfo.element) {
      return; // Couldn't find suitable location
    }

    // Generate and inject swatch HTML
    const swatchHTML = this.generateSwatchHTML(colorsetInfo, productIdentifier);

    if (swatchHTML) {
      if (targetInfo.useExisting) {
        // Use existing .secondary div - just populate it
        targetInfo.element.innerHTML = swatchHTML;
        targetInfo.element.classList.add('colorset-swatches');

        // Add click handlers for swatch navigation
        this.addSwatchClickHandlers(targetInfo.element);
      } else {
        // Create new wrapper with identifier class
        const swatchWrapper = document.createElement('div');
        swatchWrapper.className =
          'colorset-swatches secondary tw-flex tw-flex-col tw-gap-3';
        swatchWrapper.innerHTML = swatchHTML;

        targetInfo.element.appendChild(swatchWrapper);

        // Add click handlers for swatch navigation
        this.addSwatchClickHandlers(swatchWrapper);
      }

      // Add class to card__content for layout modifications
      const cardContent = cardElement.querySelector('.card__content');
      if (cardContent) {
        cardContent.classList.add('has-swatches');
      }
    }
  }

  /**
   * Find the best location to inject swatches
   */
  findSwatchTarget(cardElement) {
    // Try to find existing secondary div first
    let target = cardElement.querySelector('.secondary');
    if (target) {
      return { element: target, useExisting: true };
    }

    // Look for card information area
    target = cardElement.querySelector('.swatch-slot');
    if (target) {
      return { element: target, useExisting: false };
    }

    // Look for card information area
    target = cardElement.querySelector(
      '.card__information, .card-information, .product-info',
    );
    if (target) {
      return { element: target, useExisting: false };
    }

    // Look for card content area
    target = cardElement.querySelector(
      '.card__content, .card-content, .product-content',
    );
    if (target) {
      return { element: target, useExisting: false };
    }

    // Look for price area (common in third-party widgets)
    target = cardElement.querySelector('.price, .product-price, .rebuy-money');
    if (target) {
      return { element: target.parentElement, useExisting: false };
    }

    // Fallback: use the card element itself
    return { element: cardElement, useExisting: false };
  }

  /**
   * Add click handlers to swatch buttons for quick-add modal
   */
  addSwatchClickHandlers(container) {
    // The swatch-quick-add.js script will handle the clicks via event delegation
    // No need to add individual event listeners or modify the buttons
    // Just ensure the buttons have the correct classes and data attributes
    const swatchButtons = container.querySelectorAll('.product-swatch-button');
    swatchButtons.forEach((button) => {
      // Ensure the button has the correct class and is clickable
      if (!button.classList.contains('product-swatch-button')) {
        button.classList.add('product-swatch-button');
      }
    });
  }

  /**
   * Open quick-add modal for a product (deprecated - using swatch-quick-add.js instead)
   */
  async openQuickAddModal(productUrl) {
    // This method is no longer used - swatch-quick-add.js handles modal opening
    console.warn(
      '🎨 openQuickAddModal is deprecated - using swatch-quick-add.js instead',
    );
  }

  /**
   * Check if Advanced Commerce is currently loading products
   */
  isAdvancedCommerceLoading() {
    // Check for GrapheneHC loading indicators
    const loadingIndicators = [
      '.graphene-loading',
      '.graphene-spinner',
      '#Results:empty',
      '[data-graphene-loading="true"]',
    ];

    for (const selector of loadingIndicators) {
      if (document.querySelector(selector)) {
        return true;
      }
    }

    // Check if Results containers exist but are empty (indicating loading)
    const resultsContainers = document.querySelectorAll(
      '#Results, [id^="Results-"]',
    );
    for (const container of resultsContainers) {
      if (container && container.innerHTML.trim() === '') {
        this.loadingTargets.add(container);
        return true;
      }
    }

    return false;
  }

  /**
   * Wait for Advanced Commerce to finish loading before applying swatches
   */
  async waitForAdvancedCommerceLoad(maxWaitTime = 10000) {
    const startTime = Date.now();
    const checkInterval = 200;

    return new Promise((resolve) => {
      const checkLoading = () => {
        const elapsed = Date.now() - startTime;

        // If max wait time exceeded, proceed anyway
        if (elapsed >= maxWaitTime) {
          /*
          console.log(
            '⏰ [COLORSETS] Max wait time reached, proceeding with swatch application',
          );
					*/
          resolve();
          return;
        }

        // If still loading, check again
        if (this.isAdvancedCommerceLoading()) {
          setTimeout(checkLoading, checkInterval);
          return;
        }

        // Add a small buffer to ensure DOM is fully rendered
        setTimeout(() => {
          resolve();
        }, 300);
      };

      checkLoading();
    });
  }

  /**
   * Apply swatches to all existing product cards
   */
  async applySwatches() {
    // Wait for Advanced Commerce to finish loading if it's currently loading
    if (this.isAdvancedCommerceLoading()) {
      await this.waitForAdvancedCommerceLoad();
    }

    // Try multiple selectors to find product cards, excluding product page elements
    const selectors = [
      '.product-card-wrapper:not(.shop-the-look-card)',
      '.card-wrapper:not(.shop-the-look-card)',
      '.card:not(.product__media):not(.shop-the-look-card)',
      '.product-item:not(.shop-the-look-card)',
      '.grid__item:not(.product__media-wrapper):not(.shop-the-look-card)',
    ];

    let productCards = [];
    for (const selector of selectors) {
      const cards = document.querySelectorAll(selector);
      if (cards.length > 0) {
        // Additional filter to exclude product page media elements
        const filteredCards = Array.from(cards).filter((card) => {
          // Exclude elements that are part of product media gallery or shop-the-look cards
          if (
            card.classList.contains('product__media-wrapper') ||
            card.classList.contains('product__media') ||
            card.classList.contains('shop-the-look-card') ||
            card.classList.contains('shop-the-look-product-link') ||
            card.closest('.product__media-gallery') ||
            card.closest('.product__media-list') ||
            card.closest('[data-media-id]') ||
            card.closest('.shop-the-look-card') ||
            card.closest('.shop-the-look-container') ||
            // Additional check for modal-opener elements within shop-the-look
            (card.tagName === 'MODAL-OPENER' &&
              card.closest('.shop-the-look-card'))
          ) {
            return false;
          }
          return true;
        });
        productCards = [...productCards, ...filteredCards];
      }
    }

    // Remove duplicates
    productCards = [...new Set(productCards)];

    //console.log(`🎨 Found ${productCards.length} product cards to process`);

    let processedCount = 0;
    let skippedCount = 0;
    let appliedCount = 0;
    let noIdentifierCount = 0;
    let notInMapCount = 0;

    // Process cards sequentially to handle async operations properly
    for (let index = 0; index < productCards.length; index++) {
      const card = productCards[index];
      const productId = this.extractProductIdentifier(card);
      const colorsetInfo = productId
        ? this.productToColorsetMap.get(productId)
        : null;

      if (productId && colorsetInfo) {
        this.applySwatchesToCard(card);
        if (card.querySelector('.colorset-swatches')) {
          appliedCount++;
        }
        processedCount++;
      } else {
        skippedCount++;
        if (!productId) {
          noIdentifierCount++;
        } else {
          notInMapCount++;
        }
        if (index < 10) {
          // Log first 10 skipped cards for debugging
          /*
          console.log(`🎨 Skipped card ${index + 1}:`, {
            productId: productId || 'not found',
            hasColorsetInfo: !!colorsetInfo,
            cardClasses: card.className,
            dataHandle: card.getAttribute('data-handle'),
            dataId: card.getAttribute('data-id'),
            inMap: productId ? this.productToColorsetMap.has(productId) : false,
          });
					*/
        }
      }
    }

    /* console.log(
      `🎨 Swatches applied: ${appliedCount} processed, ${skippedCount} skipped (total: ${productCards.length})`,
    );
    console.log(
      `🎨 Breakdown: ${noIdentifierCount} no identifier, ${notInMapCount} not in map`,
    );
		*/

    // Log sample of identifiers that were extracted but not found
    if (notInMapCount > 0) {
      const sampleNotInMap = [];
      productCards.forEach((card, index) => {
        if (sampleNotInMap.length < 5) {
          const productId = this.extractProductIdentifier(card);
          if (productId && !this.productToColorsetMap.has(productId)) {
            sampleNotInMap.push(productId);
          }
        }
      });
      // console.log(`🎨 Sample identifiers not in map:`, sampleNotInMap);
    }

    // Log sample of identifiers that were extracted (for debugging)
    if (productCards.length > 0) {
      const sampleIdentifiers = [];
      productCards.forEach((card, index) => {
        if (sampleIdentifiers.length < 10) {
          const productId = this.extractProductIdentifier(card);
          if (productId) {
            sampleIdentifiers.push({
              identifier: productId,
              inMap: this.productToColorsetMap.has(productId),
              hasColorsetInfo: !!this.productToColorsetMap.get(productId),
            });
          }
        }
      });
      // console.log(`🎨 Sample extracted identifiers:`, sampleIdentifiers);
    }
  }

  /**
   * Set up MutationObserver to handle dynamically added cards
   */
  setupMutationObserver() {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = new MutationObserver((mutations) => {
      let hasNewContent = false;

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node is a product card
            if (node.matches) {
              const cardSelectors = [
                '.product-card-wrapper:not(.shop-the-look-card)',
                '.card-wrapper:not(.shop-the-look-card)',
                '.card:not(.product__media):not(.shop-the-look-card)',
                '.product-item:not(.shop-the-look-card)',
                '.grid__item:not(.product__media-wrapper):not(.shop-the-look-card)',
              ];

              if (cardSelectors.some((selector) => node.matches(selector))) {
                // Additional check to exclude shop-the-look cards
                if (
                  !node.classList.contains('shop-the-look-card') &&
                  !node.closest('.shop-the-look-card')
                ) {
                  this.applySwatchesToCard(node);
                  hasNewContent = true;
                }
              }
            }

            // Check for product cards within the added node
            const productCards =
              node.querySelectorAll &&
              node.querySelectorAll(
                '.product-card-wrapper:not(.shop-the-look-card), .card-wrapper:not(.shop-the-look-card), .card:not(.product__media):not(.shop-the-look-card), .product-item:not(.shop-the-look-card), .grid__item:not(.product__media-wrapper):not(.shop-the-look-card)',
              );
            if (productCards && productCards.length > 0) {
              // Filter out product media elements
              const filteredCards = Array.from(productCards).filter((card) => {
                if (
                  card.classList.contains('product__media-wrapper') ||
                  card.classList.contains('product__media') ||
                  card.classList.contains('shop-the-look-card') ||
                  card.closest('.product__media-gallery') ||
                  card.closest('.product__media-list') ||
                  card.closest('[data-media-id]') ||
                  card.closest('.shop-the-look-card')
                ) {
                  return false;
                }
                return true;
              });

              // Process cards sequentially
              for (const card of filteredCards) {
                this.applySwatchesToCard(card);
              }

              if (filteredCards.length > 0) {
                hasNewContent = true;
                /*console.log(
                  `🎨 MutationObserver detected ${filteredCards.length} new product cards`,
                ); */
              }
            }
          }
        }
      }

      // If we detected new content, also do a full refresh after a short delay
      if (hasNewContent) {
        clearTimeout(this.mutationRefreshTimeout);
        this.mutationRefreshTimeout = setTimeout(() => {
          /* console.log(
            '🎨 MutationObserver triggering full refresh after new content detection',
          ); */
          this.refresh();
        }, 1000);
      }
    });

    // Start observing with more comprehensive options
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false, // Don't watch attribute changes
      attributeOldValue: false,
      characterData: false,
      characterDataOldValue: false,
    });

    //console.log('MutationObserver set up for dynamic card detection');
  }

  /**
   * Public method to manually refresh swatches
   */
  async refresh() {
    if (!this.isInitialized) {
      console.warn('ColorsetSwatches not initialized yet');
      return;
    }
    await this.applySwatches();
  }

  /**
   * Debug specific product by ID, handle, or title
   */
  debugProductById(productId) {
    // console.log(`🔍 [COLORSETS] Debugging product by ID: "${productId}"`);

    // Find colorset containing this product
    let foundColorset = null;
    let foundProduct = null;

    if (this.colorsetData && this.colorsetData.colorsets) {
      for (const colorset of this.colorsetData.colorsets) {
        for (const product of colorset.products) {
          if (product.id.toString() === productId.toString()) {
            foundColorset = colorset;
            foundProduct = product;
            break;
          }
        }
        if (foundProduct) break;
      }
    }

    if (!foundProduct) {
      // console.log(
      //   `❌ Product with ID "${productId}" not found in colorsets data`,
      // );
      return;
    }
    /* 
    console.log(`✅ Found product in colorsets:`, {
      colorsetTitle: foundColorset.title,
      productTitle: foundProduct.title,
      productHandle: foundProduct.handle,
      productId: foundProduct.id,
      hasColorway: !!foundProduct.colorway,
      colorway: foundProduct.colorway,
    });
		*/
    // Check if product is in the map
    const mapEntry =
      this.productToColorsetMap.get(foundProduct.handle) ||
      this.productToColorsetMap.get(foundProduct.id.toString());
    // console.log(`📊 Product in map:`, !!mapEntry);
    if (mapEntry) {
      // console.log(`📊 Map entry:`, mapEntry);
    }

    // Look for the specific grid item
    const gridItem = document.querySelector(
      `.grid__item[data-id="${productId}"]`,
    );
    if (gridItem) {
      /*
      console.log(`🎯 Found grid item:`, {
        element: gridItem,
        classes: gridItem.className,
        hasSwatches: gridItem.querySelector('.colorset-swatches') !== null,
        hasSwatchesClass: gridItem.classList.contains('has-swatches'),
        hasSecondary: gridItem.querySelector('.secondary') !== null,
      });
			*/

      // Try to apply swatches to this specific element
      //console.log(`🎨 Attempting to apply swatches to this element...`);
      this.applySwatchesToCard(gridItem);
    } else {
      //console.log(`❌ Grid item with data-id="${productId}" not found`);
    }

    return {
      foundInColorsets: !!foundProduct,
      foundInMap: !!mapEntry,
      foundInDOM: !!gridItem,
      colorset: foundColorset,
      product: foundProduct,
      gridItem: gridItem,
    };
  }

  /**
   * Debug specific product by handle or title
   */
  debugProduct(productIdentifier) {
    //console.log(`🔍 [COLORSETS] Debugging product: "${productIdentifier}"`);

    // Find colorset containing this product
    let foundColorset = null;
    let foundProduct = null;

    if (this.colorsetData && this.colorsetData.colorsets) {
      for (const colorset of this.colorsetData.colorsets) {
        for (const product of colorset.products) {
          if (
            product.handle === productIdentifier ||
            product.title
              .toLowerCase()
              .includes(productIdentifier.toLowerCase()) ||
            product.id.toString() === productIdentifier
          ) {
            foundColorset = colorset;
            foundProduct = product;
            break;
          }
        }
        if (foundProduct) break;
      }
    }

    if (!foundProduct) {
      /*
      console.log(
        `❌ Product "${productIdentifier}" not found in colorsets data`,
      );
			*/
      return;
    }

    /*
    console.log(`✅ Found product in colorset:`, {
      colorsetTitle: foundColorset.title,
      productTitle: foundProduct.title,
      productHandle: foundProduct.handle,
      productId: foundProduct.id,
      hasColorway: !!foundProduct.colorway,
      colorway: foundProduct.colorway,
    });
		*/
    // Check if product is in the map
    const mapEntry =
      this.productToColorsetMap.get(foundProduct.handle) ||
      this.productToColorsetMap.get(foundProduct.id.toString());
    // console.log(`📊 Product in map:`, !!mapEntry);
    if (mapEntry) {
      // console.log(`📊 Map entry:`, mapEntry);
    }

    // Look for product cards on page
    const selectors = [
      `[href*="/products/${foundProduct.handle}"]`,
      `[data-product-url*="/products/${foundProduct.handle}"]`,
      `[data-product-handle="${foundProduct.handle}"]`,
      `[data-product-id="${foundProduct.id}"]`,
    ];

    // console.log(`🔍 Looking for product cards with selectors:`, selectors);

    for (const selector of selectors) {
      const elements = document.querySelectorAll(selector);
      // console.log(`  - ${selector}: ${elements.length} elements found`);

      elements.forEach((element, index) => {
        const card = element.closest(
          '.card, .product-card-wrapper, .card-wrapper, .grid__item, .product-item',
        );
        /*
        console.log(`    Element ${index + 1}:`, {
          element: element,
          card: card,
          cardClasses: card?.className,
          hasSecondary: !!card?.querySelector('.secondary'),
          hasSwatches: !!card?.querySelector('.colorset-swatches'),
          hasSwatchesClass: !!card?.querySelector('.has-swatches'),
        });
				*/
      });
    }

    return {
      foundInColorsets: !!foundProduct,
      foundInMap: !!mapEntry,
      colorset: foundColorset,
      product: foundProduct,
    };
  }

  /**
   * Check if a specific element should have swatches applied
   */
  checkElementForSwatches(element) {
    //console.log(`🔍 Checking element for swatches:`, element);

    // Get product info from the element
    const productLink = element.querySelector('a[href*="/products/"]');
    const productHandle = productLink
      ? productLink.href.split('/products/')[1]
      : null;
    const productId =
      element.getAttribute('data-id') ||
      element.getAttribute('data-product-id');
    /*
    // console.log(`📊 Element info:`, {
      productHandle: productHandle,
      productId: productId,
      classes: element.className,
      hasSwatches: element.querySelector('.colorset-swatches') !== null,
      hasSwatchesClass: element.classList.contains('has-swatches'),
    });
		*/

    // Check if product exists in colorsets
    let foundProduct = null;
    if (this.colorsetData && this.colorsetData.colorsets) {
      for (const colorset of this.colorsetData.colorsets) {
        for (const product of colorset.products) {
          if (
            (productHandle && product.handle === productHandle) ||
            (productId && product.id.toString() === productId.toString())
          ) {
            foundProduct = product;
            break;
          }
        }
        if (foundProduct) break;
      }
    }

    // console.log(`🎨 Product found in colorsets:`, !!foundProduct);
    if (foundProduct) {
      /*
      console.log(`🎨 Product details:`, {
        title: foundProduct.title,
        handle: foundProduct.handle,
        id: foundProduct.id,
        hasColorway: !!foundProduct.colorway,
      });
			*/
    }

    return {
      element: element,
      productHandle: productHandle,
      productId: productId,
      foundInColorsets: !!foundProduct,
      product: foundProduct,
    };
  }

  /**
   * Debug method to show colorset information for a product
   */
  debugProductColorsets(productHandle) {
    const colorsetInfo = this.productToColorsetMap.get(productHandle);
    if (!colorsetInfo) {
      //console.log(`❌ Product "${productHandle}" not found in colorset map`);
      return;
    }
    /*
      console.log(`🎨 Product "${productHandle}" colorset information:`, {
      productTitle: colorsetInfo.product.title,
      colorsetsCount: colorsetInfo.colorsets.length,
      colorsets: colorsetInfo.colorsets.map((cs) => ({
        id: cs.id,
        handle: cs.handle,
        productsCount: cs.products?.length || 'N/A',
      })),
      allProductsCount: colorsetInfo.allProducts.length,
      allProducts: colorsetInfo.allProducts.map((p) => ({
        handle: p.handle,
        title: p.title,
        hasColorway: !!p.colorway,
      })),
    });
		*/
  }

  /**
   * Debug method to show the overall data structure
   */
  debugDataStructure() {
    /*
    console.log(`🎨 ColorsetSwatches Data Structure:`, {
      hasColorsets: !!this.colorsetData?.colorsets,
      colorsetsCount: this.colorsetData?.colorsets?.length || 0,
      hasProducts: !!this.colorsetData?.products,
      productsCount: this.colorsetData?.products?.length || 0,
      productMapSize: this.productToColorsetMap.size,
      usingNewStructure: !!(
        this.colorsetData?.products && this.colorsetData.products.length > 0
      ),
    });
		*/
    if (this.colorsetData?.products) {
      // console.log(
      //	  `🎨 Sample products structure:`,
      //  this.colorsetData.products.slice(0, 2),
      //);
    }
  }

  /**
   * Debug helper - check system status
   */
  debug() {
    /*
    console.log('🎨 ColorsetSwatches Debug Info:');
    console.log('  - Initialized:', this.isInitialized);
    console.log('  - Colorset data loaded:', !!this.colorsetData);
    console.log(
      '  - Colorsets count:',
      this.colorsetData?.colorsets?.length || 0,
    );
    console.log('  - Product map size:', this.productToColorsetMap.size);
    console.log('  - Observer active:', !!this.observer);
		*/
    // Check for colorsets-data element
    const scriptElement = document.getElementById('colorsets-data');
    //console.log('  - colorsets-data element found:', !!scriptElement);

    // Check for product cards with multiple selectors (excluding product page media)
    const cardSelectors = [
      '.product-card-wrapper:not(.shop-the-look-card)',
      '.card-wrapper:not(.shop-the-look-card)',
      '.card:not(.product__media):not(.shop-the-look-card)',
      '.product-item:not(.shop-the-look-card)',
      '.grid__item:not(.product__media-wrapper):not(.shop-the-look-card)',
      '.product-card',
      '.rebuy-widget .rebuy-product',
      '.rebuy-product-block',
    ];

    let totalCards = 0;
    cardSelectors.forEach((selector) => {
      const cards = document.querySelectorAll(selector);
      if (cards.length > 0) {
        // Filter out product media elements
        const filteredCards = Array.from(cards).filter((card) => {
          if (
            card.classList.contains('product__media-wrapper') ||
            card.classList.contains('product__media') ||
            card.classList.contains('shop-the-look-card') ||
            card.closest('.product__media-gallery') ||
            card.closest('.product__media-list') ||
            card.closest('[data-media-id]') ||
            card.closest('.shop-the-look-card')
          ) {
            return false;
          }
          return true;
        });
        if (filteredCards.length > 0) {
          /*console.log(
            `  - ${selector}: ${filteredCards.length} found (${
              cards.length - filteredCards.length
            } excluded)`,
          ); */
          totalCards += filteredCards.length;
        }
      }
    });

    //console.log('  - Total product cards found:', totalCards);

    // Check for secondary divs
    const secondaryDivs = document.querySelectorAll('.secondary');
    //console.log('  - Secondary divs found:', secondaryDivs.length);

    // Check for any elements with product URLs
    const productUrls = document.querySelectorAll(
      '[href*="/products/"], [data-product-url]',
    );
    //console.log('  - Elements with product URLs:', productUrls.length);

    // Check for any shop-the-look cards that might have gotten has-swatches class
    const shopTheLookCards = document.querySelectorAll('.shop-the-look-card');
    // console.log('🔍 Shop-the-look cards found:', shopTheLookCards.length);
    shopTheLookCards.forEach((card, index) => {
      const hasSwatchesClass = card.querySelector('.has-swatches');
      const swatchList = card.querySelector('.colorset-swatches');
      if (hasSwatchesClass || swatchList) {
        console.error('🚨 PROBLEM FOUND: shop-the-look-card has swatches!', {
          card: card,
          hasSwatchesClass: !!hasSwatchesClass,
          swatchList: !!swatchList,
          cardClasses: card.className,
        });
      } else {
        /*
        console.log(
          `✅ Shop-the-look card ${index + 1} is clean (no swatches)`,
        );
				*/
      }
    });

    return {
      initialized: this.isInitialized,
      colorsetData: this.colorsetData,
      productMapSize: this.productToColorsetMap.size,
      productCards: totalCards,
      secondaryDivs: secondaryDivs.length,
    };
  }

  /**
   * Cleanup method
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.productToColorsetMap.clear();
    this.isInitialized = false;
  }
}

// Create global instance
window.ColorsetSwatches = new ColorsetSwatches();

// Add debug function immediately to global scope
window.debugColorsets = function () {
  return window.ColorsetSwatches.debug();
};

// console.log('🎨 ColorsetSwatches script loaded');
// console.log('🎨 Debug function available as: window.debugColorsets()');

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  //console.log('🎨 DOM loading, waiting for DOMContentLoaded...');
  document.addEventListener('DOMContentLoaded', () => {
    //console.log('🎨 DOMContentLoaded fired, initializing ColorsetSwatches...');
    window.ColorsetSwatches.init();
  });
} else {
  // DOM is already ready
  /*
  console.log(
    '🎨 DOM already ready, initializing ColorsetSwatches immediately...',
  );
	*/
  window.ColorsetSwatches.init();
}

// Multiple refresh attempts to catch late-loading content
const refreshAttempts = [500, 1000, 2000, 5000];
refreshAttempts.forEach((delay, index) => {
  setTimeout(() => {
    //console.log(`🎨 Refresh attempt ${index + 1} (${delay}ms)...`);
    if (window.ColorsetSwatches.isInitialized) {
      /*
      console.log('🎨 ColorsetSwatches initialized, refreshing...'); */
      window.ColorsetSwatches.refresh();
    } else {
      /*
      console.log(
        '🎨 ColorsetSwatches not initialized yet, attempting init...',
      ); */
      window.ColorsetSwatches.init();
    }
  }, delay);
});
